import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Coins, AlertCircle, Calendar } from 'lucide-react';
import { billingPeriodStart } from '../lib/creditHistory';

interface CreditBalanceProps {
  userId: string;
}

interface CreditData {
  credits_regular: number;
  credits_topup: number;
  next_billing_date: string;
  credits_allocated_monthly: number;
}

export function CreditBalance({ userId }: CreditBalanceProps) {
  const [loading, setLoading] = useState(true);
  const [creditData, setCreditData] = useState<CreditData | null>(null);
  const [usedThisPeriod, setUsedThisPeriod] = useState<number | null>(null);

  useEffect(() => {
    loadCredits();
  }, [userId]);

  const loadCredits = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('credits_regular, credits_topup, next_billing_date, credits_allocated_monthly')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setCreditData(data);

      if (data) {
        const periodStart = billingPeriodStart(data.next_billing_date, new Date());
        const { data: usage, error: usageError } = await supabase
          .from('credit_log')
          .select('credits_used')
          .eq('user_id', userId)
          .eq('success', true)
          .gt('credits_used', 0)
          .gte('created_at', periodStart.toISOString());

        if (usageError) {
          console.error('Error loading credit usage:', usageError);
        } else if (usage) {
          setUsedThisPeriod(usage.reduce((sum, r) => sum + r.credits_used, 0));
        }
      }
    } catch (error) {
      console.error('Error loading credits:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !creditData) {
    return null;
  }

  const totalCredits = creditData.credits_regular + creditData.credits_topup;
  const isLow = totalCredits < 500;
  const isNegative = totalCredits < 0;
  const nextBillingDate = new Date(creditData.next_billing_date);

  return (
    <div className={`bg-white rounded-2xl border p-6 ${isNegative ? 'border-red-300 bg-red-50' : isLow ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h2 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Coins className={`w-5 h-5 ${isNegative ? 'text-red-600' : isLow ? 'text-yellow-600' : 'text-gray-700'}`} />
            Credit Saldo
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Totaal beschikbaar</p>
              <p className={`text-3xl font-bold ${isNegative ? 'text-red-600' : isLow ? 'text-yellow-600' : 'text-gray-900'}`}>
                {totalCredits.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Credits per maand</p>
              <p className="text-2xl font-semibold text-gray-700">
                {creditData.credits_allocated_monthly.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-600">Regulier: </span>
              <span className="font-medium text-gray-900">{creditData.credits_regular.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-gray-600">Topup: </span>
              <span className="font-medium text-blue-600">{creditData.credits_topup.toLocaleString()}</span>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>Vernieuwt op {nextBillingDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>

          {usedThisPeriod !== null && (
            <p className="mt-2 text-sm text-gray-600">
              Deze periode gebruikt: <span className="font-semibold">{usedThisPeriod.toLocaleString('nl-NL')}</span> credits
              {(creditData.credits_allocated_monthly ?? 0) > 0 && (
                <> van {creditData.credits_allocated_monthly.toLocaleString('nl-NL')} per maand</>
              )}
            </p>
          )}

          {isNegative && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-red-100 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium mb-1">Je credits zijn op</p>
                <p>Je kunt geen nieuwe blogs maken of campagnes versturen. Koop topup credits of wacht tot je volgende refill datum.</p>
              </div>
            </div>
          )}

          {isLow && !isNegative && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-yellow-100 border border-yellow-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Je credits zijn bijna op</p>
                <p>Je hebt nog {totalCredits} credits. Overweeg topup credits aan te schaffen om zonder onderbrekingen door te kunnen werken.</p>
              </div>
            </div>
          )}
        </div>

        <div className="ml-6">
          <a
            href="https://checkout.dibbi.nl/checkout/credits"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Koop Credits
          </a>
        </div>
      </div>
    </div>
  );
}

function Plus({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );
}
